import { floor, lowerCase } from 'lodash';
import moment from 'moment';
import {
    Booking,
    EMPTY_BOOKING,
    GSLEntities,
    Participant,
    ParticipantInfoField,
    ParticipantInfoFieldType,
    Payment,
} from '../models/bookings';
import { ApplyExtras, Extras, Product } from '../models/products';
import { RezdyBooking, RezdyExtra, RezdyImage, RezdyProduct, RezdyQuantity } from '../models/rezdyObjects';
import { convertToSearchableName } from './common';

const convertRezdyImage = (productName: string, image: RezdyImage, i: number) => {
    const newImage = {
        thumbnailURL: image.thumbnailUrl ? image.thumbnailUrl : undefined,
        mediumURL: image.mediumSizeUrl,
        largeURL: image.largeSizeUrl,
        url: image.itemUrl,
        alt: `${productName} | GSL Aviation scenic flight over the Great Barrier Reef, Cairns (${i + 1})`,
    };
    return newImage;
};

const convertListOptions = (listOptions?: string): string[] => (listOptions ? listOptions?.split('\r\n') : []);

const convertExtraToParseportsType = (rezdyExtra: RezdyExtra): Extras => ({
    id: rezdyExtra.name,
    name: rezdyExtra?.name || '',
    description: rezdyExtra?.description || '',
    image: rezdyExtra?.image && {
        url: rezdyExtra.image.itemUrl,
        thumbnailURL: rezdyExtra.image.thumbnailUrl,
        alt: rezdyExtra.name,
    },
    price: floor(100 * (rezdyExtra?.price || 0)),
    appliesTo: rezdyExtra?.extraPriceType !== 'QUANTITY' ? ApplyExtras.PER_BOOKING : ApplyExtras.PER_PERSON,
});

export const convertRezdyProduct = (originalProduct: Product, rezdyProduct: RezdyProduct) => {
    const participantInfoFields = rezdyProduct?.bookingFields
        .filter((bookingField: any) => bookingField?.requiredPerParticipant)
        .reduce((acc: ParticipantInfoField[], bookingField: any) => {
            const convertedType = lowerCase(bookingField.fieldType);
            const participantInfoField: any = {
                label: bookingField.label,
                value: bookingField.value,
                fieldType: convertedType === 'string' ? ParticipantInfoFieldType.TEXT : convertedType,
            };
            if (convertedType === ParticipantInfoFieldType.LIST) {
                participantInfoField.listOptions = convertListOptions(bookingField.listOptions);
            }
            return acc.concat(participantInfoField);
        }, []) as any;
    const tempObject: Product = rezdyProduct && {
        ...originalProduct,
        productCode: rezdyProduct.productCode,
        name: rezdyProduct.name,
        cancellationPeriod: rezdyProduct.cancellationPolicyDays
            ? rezdyProduct.cancellationPolicyDays
            : originalProduct.cancellationPeriod,
        maxQuantity: rezdyProduct.quantityRequiredMax,
        searchableName: convertToSearchableName(rezdyProduct.name),
        shortDescription: rezdyProduct.shortDescription,
        longDescription: rezdyProduct.description,
        duration: rezdyProduct.durationMinutes,
        participantInfoFields: participantInfoFields,
        photos: rezdyProduct.images?.map((img, i) => convertRezdyImage(rezdyProduct.name, img, i)),
        priceOptions: rezdyProduct.priceOptions.map((option) => {
            const opt = {
                label: option.label,
                price: option.price,
                seatsUsed: option.seatsUsed,
            };
            return opt;
        }),
        displayPrice: {
            label: undefined,
            price: rezdyProduct.advertisedPrice,
        },
        extras: (rezdyProduct.extras || []).map(convertExtraToParseportsType),
        minimumNoticeMinutes: rezdyProduct.minimumNoticeMinutes,
        pickupID: rezdyProduct.pickupId,
    };
    return tempObject;
};

const participantsToRezdyParticipants = (participant: Participant, product: Product) => {
    const filterFields = participant.fields.filter((field) =>
        product.participantInfoFields.find((f) => f.label === field.label)
    );
    return {
        fields: (filterFields || []).map((field) => ({
            label: field.label,
            value: field.value,
        })),
    };
};

const extraToRezdyExtra = (extras: Extras[]) => {
    const selectedExtras = extras.filter((ex) => ex.quantity > 0);
    return selectedExtras.map((ext) => ({
        name: ext.name,
        price: floor((ext.price || 0) / 100, 2),
        quantity: ext.quantity,
    }));
};

// Which gateway took the payment, for the reservations team and reconciliation. Kept
// out of the customer-facing payment label, which stays as the bare transaction ref.
export const paymentProviderNote = (payment?: Payment) => {
    const reference = payment?.label || payment?.paymentId;
    if (!payment?.provider && !reference) {
        return undefined;
    }
    return `Paid via ${(payment?.provider || 'unknown').toUpperCase()}${reference ? `, ref ${reference}` : ''}`;
};

const composeInternalNotes = (booking: Booking) => {
    const note = paymentProviderNote(booking.payment);
    return [booking.comment, note].filter((part) => !!part).join(' ') || undefined;
};

export const convertToRezdyBooking = (booking: Booking, product: Product) => {
    const updatedParticipants = booking.participants.map((part) => participantsToRezdyParticipants(part, product));
    const rezBooking: RezdyBooking = {
        comments: booking.comment,
        customer: booking.customer,
        items: [
            {
                startTime: moment(booking.tour.startTimeLocal).toISOString(),
                extras: extraToRezdyExtra(booking.extras),
                participants: updatedParticipants,
                pickupLocation: {
                    locationName: booking.tour.pickUpLocation,
                },
                productCode: booking.tour.productCode,
                quantities: booking.quantities.map((quan) => {
                    const quantityObject: RezdyQuantity = {
                        optionLabel: quan.label,
                        optionPrice: quan.price,
                        value: quan.value,
                    };
                    return quantityObject;
                }),
            },
        ],
        payments: [
            {
                amount: booking.payment.amount,
                type: booking.payment.type,
                date: booking.payment.date,
                label: booking.payment.label,
            },
        ],
        sendNotifications: false,
        source: 'API',
        status: 'CONFIRMED',
        internalNotes: composeInternalNotes(booking),
        totalAmount: booking.totalAmount,
    };
    return rezBooking;
};

// Rebuild the internal Booking model from a live Rezdy booking (the GET /find
// response). This is the inverse of convertToRezdyBooking and is used by the
// confirmation page to load a booking's real details when there's no cached copy
// in localStorage. Note: package relationships and the website-only `isPackage`
// flag aren't stored by Rezdy, so a fetched booking is always treated as a
// single (non-package) booking.
export const convertFromRezdyBooking = (rezdyBooking: any, entity?: GSLEntities): Booking => {
    const item = (rezdyBooking?.items || [])[0] || {};
    const payment = (rezdyBooking?.payments || [])[0] || {};
    const extras: Extras[] = (item.extras || []).map((extra: RezdyExtra) => ({
        id: extra.name || '',
        name: extra.name || '',
        price: floor(100 * (extra.price || 0)),
        quantity: extra.quantity || 0,
    }));
    return {
        ...EMPTY_BOOKING,
        entity: entity || EMPTY_BOOKING.entity,
        customer: {
            firstName: rezdyBooking?.customer?.firstName || '',
            lastName: rezdyBooking?.customer?.lastName || '',
            email: rezdyBooking?.customer?.email || '',
            phone: rezdyBooking?.customer?.phone || rezdyBooking?.customer?.mobile || '',
        },
        tour: {
            productCode: item.productCode,
            productName: item.productName || '',
            startTimeLocal: item.startTimeLocal || item.startTime,
            pickUpLocation: item.pickupLocation?.locationName || '',
            pickUpTime: item.pickupLocation?.pickupTime || item.startTimeLocal || item.startTime,
        },
        quantities: (item.quantities || []).map((quan: RezdyQuantity) => ({
            label: quan.optionLabel || '',
            price: quan.optionPrice || 0,
            value: quan.value || 0,
        })),
        extras,
        payment: {
            amount: payment.amount,
            date: payment.date,
            paymentId: payment.label,
            label: payment.label,
            type: payment.type,
        },
        totalAmount: rezdyBooking?.totalAmount || 0,
        bookingResponse: { success: true, refNum: rezdyBooking?.orderNumber },
        bookingComplete: true,
        isPackage: false,
    };
};

export const createRezdyPackageBooking = (
    booking: Booking,
    packageBooking: Booking,
    product: Product,
    packageProduct: Product
) => {
    const updatedParticipants = booking.participants.map((part) =>
        participantsToRezdyParticipants(part, packageProduct)
    );
    // The package leg carries no payment of its own - it's settled on the parent
    // booking - so the note records where that payment actually went.
    const packageNote = `Booking created by ${product.entity}. Package booking. Paid on Booking ${
        booking.bookingResponse.refNum
    } with payment ref ${booking.payment?.label || booking.payment?.paymentId} via ${(
        booking.payment?.provider || 'unknown'
    ).toUpperCase()}`;
    const rezBooking: RezdyBooking = {
        comments: packageNote,
        customer: packageBooking.customer,
        items: [
            {
                startTime: moment(packageBooking.tour.startTimeLocal).toISOString(),
                participants: updatedParticipants,
                pickupLocation: {
                    locationName: packageBooking.tour.pickUpLocation,
                },
                productCode: packageProduct.productCode,
                quantities: packageBooking.quantities.map((quan) => {
                    const quantityObject: RezdyQuantity = {
                        optionLabel: quan.label,
                        optionPrice: quan.price,
                        value: quan.value,
                    };
                    return quantityObject;
                }),
            },
        ],
        payments: [
            {
                amount: 0,
                type: 'INVOICE',
                date: booking.payment.date,
            },
        ],
        sendNotifications: false,
        source: 'API',
        status: 'CONFIRMED',
        internalNotes: packageNote,
        totalAmount: 0,
    };
    return rezBooking;
};
